import AuthPhoneInput from 'Components/auth/AuthPhoneInput';
import VerifyOTP from 'Components/auth/VerifyOTPNew';
import AuthFeatureSlider from 'Components/Common/FeatureSlider/AuthFeatureSlider';
import { ANALYTICS_EVENTS } from 'constants/analyticsEvents';
import externalURLs from 'constants/externalURLs';
import { useFormik } from 'formik';
import { useAuthPhoneCountries } from 'hooks/useAuthPhoneCountries';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { featureSlides } from 'pages/Dashboard';
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Button, FormFeedback, Input, Spinner, Alert } from 'reactstrap';
import { analyticsLog, analyticsSetUser, lintrkTrack } from 'services/analytics';
import { generateLoginOTP, generateRegistrationOTP } from 'services/api/auth';
import { resetAuthFlags, setError } from 'slices/auth/reducer';
import { fetchUser, register } from 'slices/auth/thunk';
import { AppDispatch, RootState } from 'store';
import { EMPTY_AUTH_PHONE } from 'types/auth';
import { getErrorMessage, isOfficialEmail } from 'utils/helpers';
import { isValidAuthPhone } from 'utils/phone';
import * as Yup from "yup";


const Register = () => {
    const navigate = useNavigate();

    useDocumentTitle("Sign Up");

    const [loader, setLoader] = useState<boolean>(false);
    const { error } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch<AppDispatch>();

    const [agreed, setAgreed] = useState(false);

    const [agreenmentError, showAgreementError] = useState(false);

    const [otpSent, setOTPSent] = useState<boolean>(false);
    const [phone, setPhone] = useState('');
    const { countries, defaultCountry, loading: countriesLoading } = useAuthPhoneCountries();

    const [userInfo, setUserInfo] = useState({
        name: '',
        username: '',
        mobile: '',
        agency: '',
        country: EMPTY_AUTH_PHONE.country,
        country_code: EMPTY_AUTH_PHONE.country_code,
    })

    const sendOTP = async (userCredentials: { username: string }) => {
        try {
            dispatch(setError(null));
            setLoader(true);
            const status = await generateRegistrationOTP({ ...userCredentials, mode: 'email' });
            if (status) {
                setOTPSent(true)
            }
        } catch (error) {
            console.log(error)
            dispatch(setError(getErrorMessage(error, 'Failed to send OTP')));
        } finally {
            setLoader(false);
        }
    }

    const validation = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: userInfo,
        validationSchema: Yup.object({
            name: Yup.string().required('Please Enter Your Full Name').min(4, 'Name should contain minimum 4 character'),
            username: Yup.string().required("Please enter your business email address").email('Please enter a valid business email address').test('official-email', 'Please enter a valid business email address', (value) => isOfficialEmail(value)),
            agency: Yup.string().required("Please Enter Your Company Name"),
            mobile: Yup.string()
                .required("Please Enter Your Mobile Number")
                .test('valid-phone', 'Please enter a valid mobile number', function (value) {
                    return isValidAuthPhone({
                        country: this.parent.country,
                        country_code: this.parent.country_code,
                        mobile: value || '',
                    });
                }),
        }),
        onSubmit: (values) => {
            setUserInfo(values);
            sendOTP({ username: values.username });
        }
    });

    const handleVerification = async (otp: string) => {
        const response = await dispatch(register({ ...userInfo, otp }))
        if(response.meta.requestStatus === 'rejected'){
            return;
        }
        const userData = await dispatch(fetchUser()).unwrap();
        if (userData.user) {
            try {
                await analyticsSetUser(userData.user.uuid);
                lintrkTrack();
                analyticsLog(ANALYTICS_EVENTS.SIGNUP, {
                    name: userData.user.name,
                    uuid: userData.user.uuid,
                })
            } catch (error) {
                console.log(error);
            }
        }
        if (response.meta.requestStatus === 'fulfilled') {
            navigate('/');
        }
    }

    useEffect(() => {
        dispatch(resetAuthFlags());
    }, [dispatch])

    useEffect(() => {
        if (agreed)
            showAgreementError(false);
    }, [agreed])

    document.title = "Sign Up |  Influns - Where Your Influence Matters";

    return (
        <div className='d-flex vh-100 auth-page'>
            <div className="auth-illustrator-side d-flex flex-column justify-content-start justify-content-md-center align-items-center position-relative">
                <div className="grid-bg"></div>
                <div className="auth-slider-wrapper w-75">
                    <AuthFeatureSlider slides={featureSlides} />
                </div>
            </div>
            <div className="auth-side signup z-1">
                <div></div>
                <div className="auth-form">
                    {
                        !otpSent ? (
                            <React.Fragment>
                                <h2 className='auth-title'>Sign Up</h2>
                                {/* <span className="auth-helper-text">Sign In to continue to influns</span> */}
                                <div className="p-2 mt-4">
                                    <Form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            if (!agreed) {
                                                showAgreementError(true);
                                                return;
                                            }
                                            validation.handleSubmit();
                                            return false;
                                        }}
                                        className="needs-validation" action="#">

                                        {error ? (
                                            <Alert color="danger">
                                                {error} </Alert>
                                        ) : null}
                                        <div className="mb-3">
                                            <Input
                                                name="name"
                                                type="text"
                                                className='form-control auth-input'
                                                placeholder="Full Name"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.name || ""}
                                                invalid={
                                                    validation.touched.name && validation.errors.name ? true : false
                                                }
                                            />
                                            {validation.touched.name && validation.errors.name ? (
                                                <FormFeedback type="invalid"><div>{validation.errors.name}</div></FormFeedback>
                                            ) : null}

                                        </div>

                                        <div className="mb-3">
                                            <Input
                                                id="username"
                                                name="username"
                                                className="form-control auth-input"
                                                placeholder="Your Business Email Address"
                                                type="email"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.username || ""}
                                                invalid={
                                                    validation.touched.username && validation.errors.username ? true : false
                                                }
                                            />
                                            {validation.touched.username && validation.errors.username ? (
                                                <FormFeedback type="invalid"><div>{validation.errors.username}</div></FormFeedback>
                                            ) : null}

                                        </div>
                                        <div className="mb-3">
                                            {countriesLoading ? (
                                                <div className="auth-phone-input" aria-hidden="true" />
                                            ) : (
                                                <AuthPhoneInput
                                                    value={phone}
                                                    defaultCountry={defaultCountry}
                                                    countries={countries}
                                                    invalid={Boolean(validation.touched.mobile && validation.errors.mobile)}
                                                    onBlur={() => validation.setFieldTouched('mobile', true)}
                                                    onChange={(nextPhone, fields) => {
                                                        setPhone(nextPhone);
                                                        validation.setValues({
                                                            ...validation.values,
                                                            mobile: fields.mobile,
                                                            country: fields.country,
                                                            country_code: fields.country_code,
                                                        });
                                                    }}
                                                />
                                            )}
                                            {validation.touched.mobile && validation.errors.mobile ? (
                                                <FormFeedback type="invalid" className="d-block"><div>{validation.errors.mobile}</div></FormFeedback>
                                            ) : null}
                                        </div>

                                        <div className="mb-3">
                                            <Input
                                                name="agency"
                                                type="text"
                                                className='form-control auth-input'
                                                placeholder="Your Company Name"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.agency || ""}
                                                invalid={
                                                    validation.touched.agency && validation.errors.agency ? true : false
                                                }
                                            />
                                            {validation.touched.agency && validation.errors.agency ? (
                                                <FormFeedback type="invalid"><div>{validation.errors.agency}</div></FormFeedback>
                                            ) : null}

                                        </div>

                                        <div className="mb-4">
                                            <div className="d-flex align-items-center gap-2">

                                                <Input
                                                    type='checkbox'
                                                    id='agreement-check'
                                                    className='form-control auth-check-input'
                                                    checked={agreed}
                                                    onChange={(e) => setAgreed(e.target.checked)}
                                                />
                                                <label htmlFor='agreement-check' className="agreement">I agree to
                                                    <a href={externalURLs.TERMS_AND_CONDITIONS} target="_blank" rel="noreferrer" className="ms-1">Terms & Conditions</a></label>
                                            </div>
                                            {
                                                agreenmentError &&
                                                <FormFeedback type="invalid" className='d-flex'><div>Please accept the Terms and Conditions to continue.</div></FormFeedback>
                                            }
                                        </div>

                                        <div className="mt-4">
                                            <Button className="w-100 primary-gradient" type="submit" disabled={loader && true}>
                                                {loader && <Spinner size="sm" className='me-2'> Loading... </Spinner>}
                                                Sign Up
                                            </Button>
                                        </div>
                                    </Form>
                                </div>
                            </React.Fragment>
                        ) : (
                            <VerifyOTP
                                username={userInfo.username}
                                changeEmail={() => setOTPSent(false)}
                                resendOTP={async () => {
                                    return await generateLoginOTP({ username: userInfo.username });
                                }}
                                submitButtonText='Sign In'
                                handleVerification={handleVerification}
                            />
                        )
                    }
                </div>
                <div className="auth-switch">
                    <span className='auth-switch-text'>Have an Account? <Link to={{ pathname: '/login' }}>Sign In</Link></span>
                </div>
            </div>
        </div>
    )
}

export default Register